import styled from '@emotion/styled';
import {
    BodyTextSmall,
    Button,
    ButtonGroup,
    CalloutInline,
    HeadingText50,
    Icon,
    Input,
    InputType,
} from '@modernhealth/vital-components';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from 'src/shared/styles/variables';

interface Props {
    title: string;
    description: string;
    secondButtonText: string;
    secondButtonVariant?: 'primary' | 'secondary' | 'outline' | 'flat' | 'destructive';
    onBack: () => void;
    onSubmit: (response: string) => void;
    calloutMessage?: string;
}

export enum PAMMCareOptionsFormTestID {
    Container = 'PAMMCareOptionsFormTestID.Container',
    SubmitButton = 'PAMMCareOptionsFormTestID.SubmitButton',
    BackButton = 'PAMMCareOptionsFormTestID.BackButton',
}

export const PAMMCareOptionsForm: FC<Props> = ({
    title,
    description,
    secondButtonText,
    secondButtonVariant = 'primary',
    onBack,
    onSubmit,
    calloutMessage,
}) => {
    const [response, setResponse] = useState('');

    const { t } = useTranslation();

    return (
        <PageWrapper data-testid={PAMMCareOptionsFormTestID.Container}>
            <BackWrapper>
                <IconLink onClick={onBack}>
                    <Icon name="arrow-left" />
                </IconLink>
            </BackWrapper>
            <PageContent>
                <HeadingText50>{title}</HeadingText50>
                <BodyTextSmall>{description}</BodyTextSmall>
                <InputWrapper>
                    <Input
                        fieldId=""
                        type={InputType.TextArea}
                        onChange={(newValue: string) => setResponse(newValue)}
                        height={200}
                        label=""
                    />
                </InputWrapper>
                {calloutMessage && (
                    <CalloutWrapper>
                        <CalloutInline variant="default">{calloutMessage}</CalloutInline>
                    </CalloutWrapper>
                )}
                <ButtonGroup>
                    <Button
                        size="large"
                        variant="secondary"
                        onClick={onBack}
                        data-testid={PAMMCareOptionsFormTestID.BackButton}
                    >
                        {t('translation:common.back')}
                    </Button>
                    <Button
                        size="large"
                        variant={secondButtonVariant}
                        onClick={() => onSubmit(response)}
                        data-testid={PAMMCareOptionsFormTestID.SubmitButton}
                    >
                        {secondButtonText}
                    </Button>
                </ButtonGroup>
            </PageContent>
        </PageWrapper>
    );
};

const PageWrapper = styled.div({
    backgroundColor: color.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '2rem',
});

const PageContent = styled.div({
    width: '35%',
    padding: '1rem 1.5rem 0 1.5rem',
});

const InputWrapper = styled.div({
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
});

const BackWrapper = styled.div({
    width: '65%',
});

const IconLink = styled.a({
    color: 'black',
});

const CalloutWrapper = styled.div({
    marginBottom: '1.5rem',
});
